/**
 * Centralized API Registry
 * 
 * This file contains all API endpoints and their expected data structures
 * to prevent mismatches and ensure consistency across the application.
 */

// Base API configuration. When app is on localhost, use localhost for API to avoid 192.168.x.x timeouts.
const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const isAppOnLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
export const API_BASE_URL = isAppOnLocalhost ? 'http://localhost:5000/api' : envApiUrl;

// API Endpoints Registry
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    REGISTER: '/auth/register',
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    PROFILE: '/auth/profile',
  },
  
  // Admin Authentication
  ADMIN_AUTH: {
    LOGIN: '/admin/auth/login',
    LOGOUT: '/admin/auth/logout',
    PROFILE: '/admin/auth/profile',
  },
  
  // Teams
  TEAMS: {
    LIST: '/teams',
    GET: (id) => `/teams/${id}`,
    UPDATE: (id) => `/teams/${id}`,
    DELETE: (id) => `/teams/${id}`,
  },
  
  // Team Members
  MEMBERS: {
    LIST: (teamId) => `/members/${teamId}`,
    CREATE: '/members',
    UPDATE: (id) => `/members/${id}`,
    DELETE: (id) => `/members/${id}`,
  },
  
  // Admin Teams
  ADMIN_TEAMS: {
    LIST: '/admin/teams',
    GET: (id) => `/admin/teams/${id}`,
    UPDATE: (id) => `/admin/teams/${id}`,
    STATS_OVERVIEW: '/admin/stats/overview',
  },
  
  // Divisions
  DIVISIONS: {
    LIST: '/divisions',
    CREATE: '/divisions',
    UPDATE: (id) => `/divisions/${id}`,
    DELETE: (id) => `/divisions/${id}`,
    ASSIGN_TEAMS: '/divisions/assign-teams',
  },
  
  // Dashboard
  DASHBOARD: {
    OVERVIEW: '/dashboard/overview',
    UPCOMING_MATCHES: '/dashboard/upcoming-matches',
    NOTIFICATIONS: '/dashboard/notifications',
  },
};

// Canton Mapping - Centralized
export const CANTON_MAP = {
  'FR': 'Fribourg',
  'NW': 'Nidwalden', 
  'GR': 'Grisons',
  'ZH': 'Zurich',
  'BE': 'Bern',
  'LU': 'Lucerne',
  'UR': 'Uri',
  'SZ': 'Schwyz',
  'OW': 'Obwalden',
  'GL': 'Glarus',
  'ZG': 'Zug',
  'SO': 'Solothurn',
  'BS': 'Basel-Stadt',
  'BL': 'Basel-Landschaft',
  'SH': 'Schaffhausen',
  'AR': 'Appenzell Ausserrhoden',
  'AI': 'Appenzell Innerrhoden',
  'SG': 'St. Gallen',
  'AG': 'Aargau',
  'TG': 'Thurgau',
  'TI': 'Ticino',
  'VD': 'Vaud',
  'VS': 'Valais',
  'NE': 'Neuchâtel',
  'GE': 'Geneva',
  'JU': 'Jura',
  // New consolidated cantons
  'Appenzell': 'Appenzell',
  'Basel': 'Basel',
  'Nidwalden': 'Nidwalden'
};

// Canton Name Variations for filtering
export const CANTON_VARIATIONS = {
  'FR': ['Fribourg', 'fribourg', 'FR'],
  'NW': ['Nidwalden', 'nidwalden', 'NW'],
  'GR': ['Grisons', 'grisons', 'GR'],
  'ZH': ['Zurich', 'zurich', 'ZH'],
  'BE': ['Bern', 'bern', 'BE'],
  'LU': ['Lucerne', 'lucerne', 'LU'],
  'UR': ['Uri', 'uri', 'UR'],
  'SZ': ['Schwyz', 'schwyz', 'SZ'],
  'OW': ['Obwalden', 'obwalden', 'OW'],
  'GL': ['Glarus', 'glarus', 'GL'],
  'ZG': ['Zug', 'zug', 'ZG'],
  'SO': ['Solothurn', 'solothurn', 'SO'],
  'BS': ['Basel-Stadt', 'basel-stadt', 'BS'],
  'BL': ['Basel-Landschaft', 'basel-landschaft', 'BL'],
  'SH': ['Schaffhausen', 'schaffhausen', 'SH'],
  'AR': ['Appenzell Ausserrhoden', 'appenzell-ausserrhoden', 'AR'],
  'AI': ['Appenzell Innerrhoden', 'appenzell-innerrhoden', 'AI'],
  'SG': ['St. Gallen', 'st-gallen', 'SG'],
  'AG': ['Aargau', 'aargau', 'AG'],
  'TG': ['Thurgau', 'thurgau', 'TG'],
  'TI': ['Ticino', 'ticino', 'TI'],
  'VD': ['Vaud', 'vaud', 'VD'],
  'VS': ['Valais', 'valais', 'VS'],
  'NE': ['Neuchâtel', 'neuchatel', 'NE'],
  'GE': ['Geneva', 'geneva', 'GE'],
  'JU': ['Jura', 'jura', 'JU'],
  // New consolidated cantons
  'Appenzell': ['Appenzell', 'appenzell', 'AR', 'AI', 'Appenzell Ausserrhoden', 'Appenzell Innerrhoden'],
  'Basel': ['Basel', 'basel', 'BS', 'BL', 'Basel-Stadt', 'Basel-Landschaft'],
  'Nidwalden': ['Nidwalden', 'nidwalden', 'NW', 'OW', 'UR', 'Nidwalden', 'Obwalden', 'Uri'],
  // Overview table row code (see NEW_CANTONS) — was missing, so team canton "GR" did not match row "Grisons"
  'Grisons': ['Grisons', 'grisons', 'GR', 'Graubünden', 'graubunden', 'Graubünden']
};

// Data Structure Types (for reference)
export const DATA_STRUCTURES = {
  TEAM: {
    id: 'string',
    team_name: 'string',
    email: 'string',
    canton: 'string', // Proper case: "Fribourg", "Zurich", etc.
    team_skill_level: 'number | null',
    division_id: 'string | null',
    division_name: 'string | null',
    member_count: 'number',
    created_at: 'string',
    updated_at: 'string',
    is_active: 'boolean'
  },
  
  DIVISION: {
    id: 'string',
    name: 'string', // "Division A", "Division B", etc.
    canton: 'string', // Canton code: "FR", "ZH", "BE", etc.
    skill_level_min: 'number',
    skill_level_max: 'number',
    team_count: 'number',
    is_active: 'boolean',
    created_at: 'string',
    updated_at: 'string'
  },
  
  TEAM_MEMBER: {
    id: 'string',
    team_id: 'string',
    name: 'string',
    email: 'string',
    skill_level: 'number', // 1.0 to 8.0
    position: 'string',
    created_at: 'string',
    updated_at: 'string'
  }
};

// Helper Functions
export const getCantonName = (code) => CANTON_MAP[code] || code;
export const getCantonCode = (name) => {
  const entry = Object.entries(CANTON_MAP).find(([code, cantonName]) => 
    cantonName === name || code === name
  );
  return entry ? entry[0] : name;
};

export const getCantonVariations = (canton) => {
  return CANTON_VARIATIONS[canton] || [canton];
};

// API Call Helpers
export const buildQueryParams = (params) => {
  const filtered = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  
  return new URLSearchParams(filtered).toString();
};

// Debug Helper
export const debugApiCall = (endpoint, params, response) => {
  console.log(`🔍 API Call Debug:`, {
    endpoint,
    params,
    response: response?.data,
    status: response?.status
  });
};
